import * as React from "react";
import MUIDataTable, {
  MUIDataTableColumn,
  MUIDataTableOptions,
} from "mui-datatables";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import {
  Autocomplete,
  Box,
  Card,
  FormControl,
  Grid,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import axios from "../../api";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import SoftBox from "components/SoftBox";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftButton from "components/SoftButton";
import AddAsset from "./AddAsset";
import SoftTypography from "components/SoftTypography";
import SlideOverPanel from "components/SlideOverPanel";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Visibility as VisibilityIcon } from "@mui/icons-material";
import BulkUploadAssets from "./BulkUploadAssets";
import { AxiosError } from "axios";

const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

// Table options
const tableOptions: MUIDataTableOptions = {
  selectableRows: "none",
  selectableRowsHeader: false,
  elevation: 0,
  tableBodyHeight: "auto",
  responsive: "standard",
};

interface AssetTypeItem {
  asset_type_id: number;
  asset_type_name: string;
}

interface Asset {
  asset_id: number;
}

interface Assets {
  asset_id: number;
  is_active: number | boolean;
}

// Component definitions moved OUTSIDE parent component
interface BrandNameCellProps {
  value?: string;
}

const BrandNameCell: React.FC<BrandNameCellProps> = ({ value }) => {
  const assetName = value || "";
  const displayValue =
    assetName.length > 20 ? `${assetName.substring(0, 20)}...` : assetName;
  return (
    <Tooltip title={assetName}>
      <span>{displayValue}</span>
    </Tooltip>
  );
};

interface AssetStatusCellProps {
  value?: string;
}

const AssetStatusCell: React.FC<AssetStatusCellProps> = ({ value }) => {
  if (!value || typeof value !== "string") return <span>-</span>;

  const formatted = value
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

  return (
    <span>
      {formatted
        .replace("In Use No Subscription", "In Use (No Subscription)")
        .replace("Out Of Maintenance", "Out of Maintenance")
        .replace("Out Of Use", "Out of Use")
        .replace("Not Installed", "Not Installed")}
    </span>
  );
};

// Renderer functions moved OUTSIDE parent component
const renderBrandNameCell = (value: string) => <BrandNameCell value={value} />;

const renderAssetStatusCell = (value: string) => (
  <AssetStatusCell value={value} />
);

const renderActionsCell = (
  filteredRows: Assets[],
  rowIndex: number,
  handleView: (asset: Asset) => void
) => {
  const data = filteredRows[rowIndex];

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <IconButton aria-label="view" onClick={() => handleView(data)}>
        <VisibilityIcon />
      </IconButton>
    </Box>
  );
};

const AssetsList = () => {
  const [searchParams] = useSearchParams();
  const status = searchParams.get("status");
  const navigate = useNavigate();
  const [rows, setRows] = React.useState<Assets[]>([]);
  const [error, setError] = React.useState<AxiosError | null>(null);
  const [isAddOpen, setAddOpen] = React.useState(false);
  const [assetTypes, setAssetTypes] = React.useState(null);
  const [assetTypeFilter, setAssetTypeFilter] =
    React.useState<AssetTypeItem | null>(null);
  const [isBulkUploadOpen, setBulkUploadOpen] = React.useState(false);

  const filteredRows = React.useMemo(() => {
    if (!status) return rows;

    if (status === "active") {
      return rows.filter((c) => Number(c.is_active) === 1);
    }

    if (status === "inactive") {
      return rows.filter((c) => Number(c.is_active) === 0);
    }

    return rows;
  }, [rows, status]);

  const handleAddOpen = () => {
    setAddOpen(true);
  };

  const handleAddClose = () => {
    setAddOpen(false);
  };

  const handleView = (asset: Asset) => {
    navigate(`/assets/view/${asset.asset_id}`);
  };

  const handleBulkUploadClose = () => {
    setBulkUploadOpen(false);
  };

  const columns: MUIDataTableColumn[] = React.useMemo(
    () => [
      { name: "asset_id", label: "Asset ID", options: { display: false } },
      { name: "asset_service_id", label: "Asset Id" },
      { name: "asset_type_name", label: "Asset Type" },
      {
        name: "asset_name",
        label: "Brand Name",
        options: {
          filter: false,
          customBodyRender: renderBrandNameCell,
        },
      },
      {
        name: "asset_status",
        label: "Asset Status",
        options: {
          filter: true,
          sort: true,
          customBodyRender: renderAssetStatusCell,
        },
      },
      { name: "customer_name", label: "Customer" },
      { name: "asset_sku", label: "SKU" },
      {
        name: "Actions",
        label: "Actions",
        options: {
          filter: false,
          sort: false,
          customBodyRender: (value, tableMeta) =>
            renderActionsCell(filteredRows, tableMeta.rowIndex, handleView),
        },
      },
    ],
    [filteredRows]
  );

  const fetchData = React.useCallback(async () => {
    try {
      const response = await axios.get("/assets", {
        params: {
          filters: {
            asset_type_id: assetTypeFilter?.asset_type_id,
          },
        },
      });

      setRows(response?.data?.list);
    } catch (error) {
      if (error instanceof AxiosError && error) {
        setError(error);
      }
    }
  }, [assetTypeFilter]);

  React.useEffect(() => {
    fetchData();
  }, [assetTypeFilter, fetchData]);

  const fetchAssets = async () => {
    try {
      const assetTypesData = await axios.get("/asset-types");
      setAssetTypes(assetTypesData?.data?.list);
    } catch (error) {
      console.error("Error: ", error);
    }
  };

  React.useEffect(() => {
    fetchAssets();
  }, []);

  const [customers, setCustomers] = React.useState(null);
  const fetchCustomersData = React.useMemo(
    () => async () => {
      try {
        const customerResponse = await axios.get("/customer", {
          params: {
            is_active: 1,
          },
        });
        setCustomers(customerResponse?.data?.list);
      } catch (error) {
        if (error instanceof AxiosError && error) {
          setError(error);
        }
      }
    },
    []
  );

  React.useEffect(() => {
    fetchData();
    fetchCustomersData();
  }, [fetchData, fetchCustomersData]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card
        style={{ padding: "24px", margin: "16px auto", maxWidth: "2000px" }}
      >
        <Grid
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "8px",
          }}
        >
          <FormControl sx={{ marginBottom: "3px" }}>
            <SoftTypography
              variant="caption"
              fontWeight="bold"
              sx={{ paddingBottom: "10px" }}
            >
              Asset Types
            </SoftTypography>
            <Autocomplete
              options={assetTypes ?? []}
              getOptionLabel={(option) => option.asset_type_name}
              value={assetTypeFilter}
              onChange={(event, value) => {
                setAssetTypeFilter(value);
              }}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" />
              )}
            />
          </FormControl>
          <Box sx={{ display: "flex", gap: 2 }}>
            <SoftButton variant="gradient" color="info" onClick={handleAddOpen}>
              Add Asset
            </SoftButton>
          </Box>
        </Grid>
        <SoftBox
          sx={{
            "& .MuiTableRow-root:not(:last-child)": {
              "& td": (theme: Theme) => ({
                borderBottom: `${theme.borders.borderWidth[1]} solid ${theme.borders.borderColor}`,
              }),
            },
          }}
          className="manage-lifts"
        >
          <ThemeProvider theme={getMuiTheme}>
            <MUIDataTable
              title={"Manage Assets"}
              data={filteredRows}
              columns={columns}
              options={tableOptions}
            />
          </ThemeProvider>
          {error && <div>Error: {error.message}</div>}
        </SoftBox>
      </Card>

      <SlideOverPanel
        open={isAddOpen}
        onClose={handleAddClose}
        title="Add Asset"
        width={1000}
      >
        <AddAsset
          isOpen={true}
          onClose={handleAddClose}
          fetchData={() => {
            handleAddClose();
            fetchData();
          }}
          customers={customers}
          embedded
        />
      </SlideOverPanel>
      {isBulkUploadOpen && (
        <BulkUploadAssets
          isOpen={isBulkUploadOpen}
          onClose={handleBulkUploadClose}
          fetchData={fetchData}
        />
      )}
    </DashboardLayout>
  );
};

export default AssetsList;
